import { FeatureAppLoader } from '@feature-hub/react';
import React from 'react';
import { createGlobalStyle } from 'styled-components';
import { Helmet, HelmetData, HelmetProvider } from 'react-helmet-async';
import { Route, Routes } from 'react-router-dom';
import { Footer } from './components/footer';
import { Header } from './components/header';
import './static/fonts.css';
import favicon from './static/favicon.ico';
import { isError } from './utils/errors';
import { ErrorMessage } from './components/error-message';
import { ContentContainer } from './components/content-container';
import '@adac-de/component-library/dist/styles.css';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-family: sans-serif;
    -ms-text-size-adjust: 100%;
    -webkit-text-size-adjust: 100%;
  }

  body {
    background: #fff;
    font-family: MiloADAC, sans-serif;
    font-weight: normal;
    font-feature-settings: "lnum";
  }
`;

const PreloadFonts = () => {
  const preloadFonts = new Set([
    'https://assets.adac.de/raw/upload/fonts/MiloADAC-bold.woff2',
    'https://assets.adac.de/raw/upload/fonts/MiloADAC-italic.woff2',
    'https://assets.adac.de/raw/upload/fonts/MiloADAC-medium.woff2',
    'https://assets.adac.de/raw/upload/fonts/MiloADAC.woff2',
    'https://assets.adac.de/raw/upload/fonts/MiloSlabADAC.woff2'
  ]);

  return [...preloadFonts].map((url: string) => (
    <link
      key={`font_${url}`}
      rel="preload"
      as="font"
      type="font/woff2"
      href={url}
      crossOrigin="anonymous"
    />
  ));
};

type FeatureAppRouteProps = {
  baseUrl?: string;
  id: string;
  fileName: string;
  config?: Record<string, string | undefined>;
};

const FeatureAppRoute: React.FC<FeatureAppRouteProps> = ({ baseUrl, id, fileName, config }) => (
  <FeatureAppLoader
    baseUrl={baseUrl}
    featureAppId={`adac:feature-app:${id}`}
    src={`/apps/${fileName}.client.js`}
    serverSrc={baseUrl ? `/apps/${fileName}.ssr.js` : undefined}
    css={[{ href: '/apps/styles.css' }]}
    config={config}
  >
    {({ error, loading, featureAppNode }) => {
      if (isError(error)) {
        // eslint-disable-next-line no-console
        console.error(`Error: ${error.message}`);
        return (
          <ContentContainer>
            <ErrorMessage
              headline="Der Solarberater kann zur Zeit nicht geladen werden."
              subline="Bitte versuchen Sie ein Browser refresh oder versuchen Sie es später noch mal."
            />
          </ContentContainer>
        );
      }

      return (
        <>
          <ContentContainer style={{ display: loading ? 'none' : 'block' }}>
            {featureAppNode}
          </ContentContainer>
          {loading && <ContentContainer>loading..</ContentContainer>}
        </>
      );
    }}
  </FeatureAppLoader>
);

const ContentWrapper: React.FC<
  React.PropsWithChildren<{
    helmetContext?: HelmetData['context'];
  }>
> = ({ helmetContext, children }) => (
  <HelmetProvider context={helmetContext}>
    <Helmet>
      <title>e-Charge Feature App | ADAC</title>
      <meta name="robots" content="none" />
      {PreloadFonts()}
      <link rel="shortcut icon" type="image/x-icon" href={favicon} />{' '}
    </Helmet>
    <GlobalStyle />
    <Header />
    {children}
    <Footer />
  </HelmetProvider>
);

export const App: React.FC<{
  helmetContext?: HelmetData['context'];
  baseUrl?: string;
  environment?: string;
}> = ({ helmetContext, baseUrl, environment }) => (
  <ContentWrapper helmetContext={helmetContext}>
    <Routes>
      <Route
        path="/*"
        element={
          <FeatureAppRoute
            baseUrl={baseUrl}
            fileName="e-charge"
            id="e-charge"
            config={{ environment }}
          />
        }
      />
    </Routes>
  </ContentWrapper>
);

export const ErrorPage: React.FC<{ helmetContext?: HelmetData['context']; error: Error }> = ({
  helmetContext,
  error
}) => <ContentWrapper helmetContext={helmetContext}>Error: {error.message}</ContentWrapper>;
